import { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import {
  FiSend,
  FiFileText,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiEye,
  FiClock,
  FiUsers,
  FiPaperclip,
  FiAlertTriangle,
  FiX,
  FiDownload,
  FiRefreshCw,
  FiLayers,
} from "react-icons/fi";
import communicationService from "../../../services/communicationService";
import CommunicationForm from "../../Communication/CommunicationForm";

const TYPE_CONFIG = {
  circular: { label: "Circular", icon: FiFileText, bg: "#e8f1f8", color: "#003b67", border: "#b6d5ec" },
  timetable_change: { label: "Timetable Change", icon: FiClock, bg: "#fef3c7", color: "#d97706", border: "#fde68a" },
  notice: { label: "Notice", icon: FiLayers, bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" },
  message: { label: "Message", icon: FiUsers, bg: "#f5f3ff", color: "#7c3aed", border: "#ddd6fe" },
  urgent_alert: { label: "Urgent Alert", icon: FiAlertTriangle, bg: "#fef2f2", color: "#dc2626", border: "#fecaca" },
};

const PRIORITY_CONFIG = {
  normal: { label: "Normal", bg: "#f1f5f9", color: "#475569" },
  high: { label: "High Priority", bg: "#fff7ed", color: "#c2410c" },
  urgent: { label: "URGENT", bg: "#fee2e2", color: "#991b1b" },
};

export default function TeacherCommunication() {
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [activeTab, setActiveTab] = useState("inbox"); // "inbox", "sent", "all"
  const [selectedType, setSelectedType] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Modals
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [viewItem, setViewItem] = useState(null);

  const loadCommunications = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        tab: activeTab,
        type: selectedType !== "all" ? selectedType : undefined,
        search: searchQuery.trim() || undefined,
      };

      const res = await communicationService.getAll(params);
      const list = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res)
        ? res
        : [];
      setCommunications(list);
    } catch (err) {
      console.error("Error loading teacher communications:", err);
      toast.error(err.response?.data?.message || err.message || "Failed to load communications");
    } finally {
      setLoading(false);
    }
  }, [activeTab, selectedType, searchQuery]);

  useEffect(() => {
    loadCommunications();
  }, [loadCommunications]);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;

    try {
      await communicationService.delete(id);
      toast.success("Communication deleted successfully");
      loadCommunications();
    } catch (err) {
      console.error("Delete error:", err);
      toast.error(err.response?.data?.message || "Failed to delete communication");
    }
  };

  const handleOpenDetail = (item) => {
    setViewItem(item);
    if (!item.isRead) {
      communicationService.markAsRead(item._id).catch(() => {});
    }
  };

  const renderAudienceTags = (comm) => {
    const aud = comm.targetAudience || {};
    const roles = aud.roles || [];
    const tags = [];

    if (roles.includes("employees")) {
      tags.push({ label: "👥 Staff / Teachers", bg: "#ecfdf5", color: "#047857" });
    }

    if (roles.includes("students")) {
      let detail = "";
      if (!aud.allCourses && aud.courses?.length > 0) {
        detail += aud.courses.map((c) => c.courseCode || c.courseName).join(", ");
      } else {
        detail += "All Courses";
      }

      if (!aud.allYears && aud.years?.length > 0) {
        detail += ` · Yr ${aud.years.join(",")}`;
      }

      tags.push({ label: `🎓 Students (${detail})`, bg: "#e8f1f8", color: "#002747" });
    }

    return tags.map((t, idx) => (
      <span
        key={idx}
        style={{
          fontSize: 11.5,
          fontWeight: 600,
          background: t.bg,
          color: t.color,
          padding: "2px 7px",
          borderRadius: 5,
        }}
      >
        {t.label}
      </span>
    ));
  };

  return (
    <div>
      {/* ── Page Header ── */}
      <div
        className="page-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "1rem",
          marginBottom: "1.25rem",
        }}
      >
        <div>
          <h1 className="page-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FiSend color="#7c3aed" /> Communication &amp; Circulars
          </h1>
          <p className="page-subtitle">
            View staff notices and send circulars or timetable updates directly to students
          </p>
        </div>

        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <button
            className="btn btn-outline btn-sm"
            onClick={loadCommunications}
            style={{ display: "flex", alignItems: "center", gap: 5 }}
          >
            <FiRefreshCw size={13} /> Refresh
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShowComposeModal(true)}
            style={{ display: "flex", alignItems: "center", gap: 6, background: "#7c3aed", borderColor: "#7c3aed" }}
          >
            <FiPlus size={15} /> Send Notice to Students
          </button>
        </div>
      </div>

      {/* ── Filter Card ── */}
      <div className="card" style={{ padding: "1rem 1.25rem", marginBottom: "1.25rem" }}>
        {/* Navigation Tabs */}
        <div
          style={{
            display: "flex",
            gap: "0.5rem",
            borderBottom: "1px solid #e2e8f0",
            paddingBottom: "0.75rem",
            marginBottom: "1rem",
          }}
        >
          <button
            type="button"
            onClick={() => setActiveTab("inbox")}
            style={{
              padding: "6px 14px",
              borderRadius: 6,
              border: "none",
              background: activeTab === "inbox" ? "#7c3aed" : "transparent",
              color: activeTab === "inbox" ? "#fff" : "#475569",
              fontWeight: 600,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Received Staff Notices
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("sent")}
            style={{
              padding: "6px 14px",
              borderRadius: 6,
              border: "none",
              background: activeTab === "sent" ? "#7c3aed" : "transparent",
              color: activeTab === "sent" ? "#fff" : "#475569",
              fontWeight: 600,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Sent to Students by Me
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("all")}
            style={{
              padding: "6px 14px",
              borderRadius: 6,
              border: "none",
              background: activeTab === "all" ? "#7c3aed" : "transparent",
              color: activeTab === "all" ? "#fff" : "#475569",
              fontWeight: 600,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            All Messages
          </button>
        </div>

        {/* Filter Row */}
        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ flex: 1, minWidth: 220, position: "relative" }}>
            <FiSearch
              size={15}
              style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }}
            />
            <input
              type="text"
              className="form-control"
              placeholder="Search circulars, subject..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ paddingLeft: 32, fontSize: 13 }}
            />
          </div>

          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="form-control"
            style={{ width: "auto", minWidth: 150, fontSize: 13 }}
          >
            <option value="all">All Types</option>
            <option value="circular">📜 Circulars</option>
            <option value="timetable_change">⏰ Timetable Changes</option>
            <option value="notice">📢 Notices</option>
            <option value="message">💬 Messages</option>
            <option value="urgent_alert">🚨 Urgent Alerts</option>
          </select>
        </div>
      </div>

      {/* ── Feed List ── */}
      {loading ? (
        <div className="card" style={{ padding: "2.5rem", textAlign: "center", color: "var(--text-muted)" }}>
          <div className="spinner" style={{ margin: "0 auto 12px" }} />
          Loading circulars and messages…
        </div>
      ) : communications.length === 0 ? (
        <div className="card" style={{ padding: "3rem 1.5rem", textAlign: "center" }}>
          <div
            style={{
              width: 50,
              height: 50,
              borderRadius: "50%",
              background: "#f5f3ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 12px",
              color: "#7c3aed",
            }}
          >
            <FiSend size={22} />
          </div>
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 6px" }}>No communications found</h3>
          <p style={{ fontSize: 13, color: "var(--text-muted)", maxWidth: 380, margin: "0 auto 14px" }}>
            {activeTab === "sent"
              ? "You haven't sent any messages or timetable updates to students yet."
              : "No notices or circulars available at this moment."}
          </p>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShowComposeModal(true)}
            style={{ background: "#7c3aed", borderColor: "#7c3aed" }}
          >
            <FiPlus size={14} /> Send Circular to Students
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
          {communications.map((comm) => {
            const typeConf = TYPE_CONFIG[comm.type] || TYPE_CONFIG.circular;
            const prioConf = PRIORITY_CONFIG[comm.priority] || PRIORITY_CONFIG.normal;
            const TypeIcon = typeConf.icon;

            return (
              <div
                key={comm._id}
                className="card"
                style={{
                  padding: "1.1rem 1.25rem",
                  borderLeft: `4px solid ${typeConf.color}`,
                  transition: "all 0.15s ease",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem", flexWrap: "wrap" }}>
                  <div style={{ display: "flex", gap: "0.85rem", flex: 1, minWidth: 260 }}>
                    <span
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        background: typeConf.bg,
                        border: `1px solid ${typeConf.border}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: typeConf.color,
                        flexShrink: 0,
                      }}
                    >
                      <TypeIcon size={17} />
                    </span>

                    <div style={{ flex: 1 }}>
                      {/* Top Badges */}
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap", marginBottom: 4 }}>
                        <span
                          style={{
                            fontSize: 10.5,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            padding: "2px 6px",
                            borderRadius: 4,
                            background: typeConf.bg,
                            color: typeConf.color,
                          }}
                        >
                          {typeConf.label}
                        </span>

                        {comm.priority !== "normal" && (
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              textTransform: "uppercase",
                              padding: "2px 6px",
                              borderRadius: 4,
                              background: prioConf.bg,
                              color: prioConf.color,
                            }}
                          >
                            {prioConf.label}
                          </span>
                        )}

                        <span style={{ fontSize: 12, color: "#94a3b8" }}>&bull;</span>
                        <span style={{ fontSize: 12, color: "#64748b" }}>
                          {new Date(comm.createdAt).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </span>
                      </div>

                      {/* Title */}
                      <h3
                        style={{
                          fontSize: 14.5,
                          fontWeight: 700,
                          color: "#1e293b",
                          margin: "0 0 6px",
                          cursor: "pointer",
                        }}
                        onClick={() => handleOpenDetail(comm)}
                      >
                        {comm.title}
                      </h3>

                      {/* Message Snippet */}
                      <p
                        style={{
                          fontSize: 13,
                          color: "#475569",
                          margin: "0 0 8px",
                          lineHeight: 1.45,
                          whiteSpace: "pre-wrap",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {comm.message}
                      </p>

                      {/* Target Audience Badges & Sender */}
                      <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                        <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", alignItems: "center" }}>
                          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>To:</span>
                          {renderAudienceTags(comm)}
                        </div>

                        <span style={{ fontSize: 12, color: "#cbd5e1" }}>|</span>

                        <span style={{ fontSize: 12, color: "#64748b" }}>
                          From: <strong>{comm.senderName || "Staff"}</strong>
                        </span>

                        {comm.attachments?.length > 0 && (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              fontSize: 11,
                              fontWeight: 600,
                              color: "#003b67",
                              background: "#e8f1f8",
                              padding: "2px 6px",
                              borderRadius: 4,
                            }}
                          >
                            <FiPaperclip size={11} /> {comm.attachments.length} file{comm.attachments.length > 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => handleOpenDetail(comm)}
                      style={{ fontSize: 12, padding: "4px 9px" }}
                    >
                      <FiEye size={12} /> View
                    </button>

                    {comm.isMine && (
                      <button
                        className="btn btn-outline btn-sm"
                        onClick={() => handleDelete(comm._id, comm.title)}
                        style={{ color: "#dc2626", borderColor: "#fca5a5", fontSize: 12, padding: "4px 7px" }}
                        title="Delete Communication"
                      >
                        <FiTrash2 size={12} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── COMPOSE MODAL ── */}
      {showComposeModal && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(2px)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
          }}
        >
          <div
            className="modal-content"
            style={{
              maxWidth: 780,
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
              padding: "1.5rem",
              borderRadius: 12,
              background: "#fff",
              boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0, display: "flex", alignItems: "center", gap: 8, color: "#7c3aed" }}>
                <FiSend /> Send Circular / Message to Students
              </h2>
              <button
                onClick={() => setShowComposeModal(false)}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b" }}
              >
                <FiX size={20} />
              </button>
            </div>

            <CommunicationForm
              isModal
              onSuccess={() => {
                setShowComposeModal(false);
                setActiveTab("sent");
                loadCommunications();
              }}
              onCancel={() => setShowComposeModal(false)}
            />
          </div>
        </div>
      )}

      {/* ── DETAIL MODAL ── */}
      {viewItem && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(2px)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
          }}
        >
          <div
            className="modal-content"
            style={{
              maxWidth: 680,
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
              padding: "1.75rem",
              borderRadius: 12,
              background: "#fff",
              boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: 6 }}>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      padding: "2px 8px",
                      borderRadius: 4,
                      background: TYPE_CONFIG[viewItem.type]?.bg || "#e8f1f8",
                      color: TYPE_CONFIG[viewItem.type]?.color || "#003b67",
                    }}
                  >
                    {TYPE_CONFIG[viewItem.type]?.label || viewItem.type}
                  </span>

                  {viewItem.priority !== "normal" && (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        padding: "2px 8px",
                        borderRadius: 4,
                        background: PRIORITY_CONFIG[viewItem.priority]?.bg || "#fee2e2",
                        color: PRIORITY_CONFIG[viewItem.priority]?.color || "#dc2626",
                      }}
                    >
                      {PRIORITY_CONFIG[viewItem.priority]?.label || viewItem.priority}
                    </span>
                  )}
                </div>

                <h2 style={{ fontSize: 17, fontWeight: 800, color: "#0f172a", margin: 0 }}>
                  {viewItem.title}
                </h2>
              </div>

              <button
                onClick={() => setViewItem(null)}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b" }}
              >
                <FiX size={20} />
              </button>
            </div>

            {/* Meta Info */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.4rem",
                padding: "0.85rem",
                background: "#f8fafc",
                borderRadius: 8,
                fontSize: 12.5,
                color: "#475569",
                marginBottom: "1.25rem",
              }}
            >
              <div>
                <strong>Sent By:</strong> {viewItem.senderName} ({viewItem.senderRole})
              </div>
              <div>
                <strong>Date:</strong>{" "}
                {new Date(viewItem.createdAt).toLocaleDateString("en-IN", {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <strong>Audience:</strong> {renderAudienceTags(viewItem)}
              </div>
            </div>

            {/* Message Body */}
            <div
              style={{
                fontSize: 14,
                lineHeight: 1.7,
                color: "#1e293b",
                whiteSpace: "pre-wrap",
                marginBottom: "1.5rem",
                padding: "0 4px",
              }}
            >
              {viewItem.message}
            </div>

            {/* Attachments */}
            {viewItem.attachments?.length > 0 && (
              <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: "1rem" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                  <FiPaperclip size={14} /> Attachments ({viewItem.attachments.length}):
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  {viewItem.attachments.map((att, idx) => (
                    <a
                      key={idx}
                      href={att.url}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "8px 12px",
                        background: "#e8f1f8",
                        borderRadius: 6,
                        border: "1px solid #b6d5ec",
                        textDecoration: "none",
                        color: "#002747",
                        fontSize: 13,
                        fontWeight: 600,
                      }}
                    >
                      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <FiFileText size={15} /> {att.name || "Download Document"}
                      </span>
                      <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#003b67" }}>
                        <FiDownload size={13} /> Open
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1.5rem" }}>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => setViewItem(null)}
                style={{ background: "#7c3aed", borderColor: "#7c3aed" }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
